"use client"
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  BookOpen,
  CheckSquare,
  Calendar,
  GraduationCap,
  ClipboardList,
  BarChart3,
  Zap,
  AlertCircle,
  Loader2,
  ChevronDown,
  Copy,
  X,
  Plus,
  Clock,
  User,
  Trash2,
  Building2,
  Check,
} from "lucide-react"

// Import your actual API functions
import { postAws } from "../aws"
import { getStudentInfo, sendEnrollmentRequest } from "./api"

const StudentDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [flashMessage, setFlashMessage] = useState(null)
  const [showAllPendingTasks, setShowAllPendingTasks] = useState(false)
  const [selectedClass, setSelectedClass] = useState(null)
  const [showClassDetails, setShowClassDetails] = useState(false)
  const [showAllPendingTasksModal, setShowAllPendingTasksModal] = useState(false)

  // Class enrollment states
  const [enrollmentRequests, setEnrollmentRequests] = useState([])
  const [newClassCode, setNewClassCode] = useState("")
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showJoinClassModal, setShowJoinClassModal] = useState(false)
  const [showPendingRequestsModal, setShowPendingRequestsModal] = useState(false)

  // Organization states - completely separate from class enrollment
  const [studentOrgStatus, setStudentOrgStatus] = useState(null)
  const [studentOrgName, setStudentOrgName] = useState(null)
  const [studentOrgId, setStudentOrgId] = useState(null)
  const [showOrgEnrollmentModal, setShowOrgEnrollmentModal] = useState(false)
  const [showOrgStatusModal, setShowOrgStatusModal] = useState(false)
  const [orgRequestData, setOrgRequestData] = useState({
    organisationId: "",
  })
  const [isSubmittingOrgRequest, setIsSubmittingOrgRequest] = useState(false)
  const [orgRequestMessage, setOrgRequestMessage] = useState(null)

  const navigate = useNavigate()

  // Check if user has enrolled classes
  const hasEnrolledClasses = dashboardData?.classes?.length > 0 || false

  // Fetch dashboard data using the new single API endpoint
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true)
      const studentId = localStorage.getItem("studentId")
      if (!studentId) {
        setFlashMessage({
          type: "error",
          message: "Student ID not found. Please login again.",
        })
        return
      }

      // Single API call to get all dashboard data
      const response = await postAws("https://api-dev.aurait.ai/classroom", "POST", {
        request: "return-student-dashboard",
        body: {
          studentId: studentId,
        },
      })

      if (response.statusCode === 200) {
        const parsedBody = JSON.parse(response.body)
        const data = parsedBody.data
        setDashboardData(data)

        // Extract organization details from the top-level response body
        const orgRequestStatus = parsedBody.organisationRequest
        const orgName = parsedBody.organisationName
        const orgId = parsedBody.organisationId

        setStudentOrgStatus(orgRequestStatus)
        setStudentOrgName(orgName)
        setStudentOrgId(orgId)

        // If organization is approved, save to local storage
        if (orgRequestStatus === "approved" && orgId && orgName) {
          localStorage.setItem("organisationId", orgId)
          localStorage.setItem("organisationName", orgName)
        } else {
          // Clear local storage if not approved or no org
          localStorage.removeItem("organisationId")
          localStorage.removeItem("organisationName")
        }

        // Also fetch enrollment requests (for classes)
        const studentInfoResponse = await getStudentInfo()
        if (studentInfoResponse.statusCode === 200 || studentInfoResponse.statusCode === 201) {
          const studentData = studentInfoResponse.body.data
          setEnrollmentRequests(Array.isArray(studentData.enrollmentRequest) ? studentData.enrollmentRequest : [])
        }
      } else {
        throw new Error(JSON.parse(response.body)?.message || "Failed to fetch dashboard data")
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to fetch dashboard data. Please try again.",
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  // Organization enrollment handler - completely separate from class enrollment
  const handleOrgEnrollmentSubmit = async (e) => {
    e.preventDefault()
    setIsSubmittingOrgRequest(true)
    try {
      const studentId = localStorage.getItem("studentId")
      const student = dashboardData?.student

      // Check if student has enrolled classes and show warning
      const hasEnrolledClasses = dashboardData?.classes?.length > 0
      if (hasEnrolledClasses && !orgRequestData.confirmed) {
        setOrgRequestMessage({
          type: "warning",
          text: "Warning: If you attach yourself to an organization, your current class data may be affected. We recommend creating a new account with a different ID to join an organization instead.",
          needsConfirmation: true,
        })
        setIsSubmittingOrgRequest(false)
        return
      }

      const requestData = {
        request: "student-admin-enroll",
        body: {
          userId: studentId,
          name: student?.studentName,
          email: student?.studentEmail,
          phone: student?.phoneNumber,
          organisationId: orgRequestData.organisationId,
        },
      }

      const response = await postAws("https://api-dev.aurait.ai/orgnaisational-admin", "POST", requestData)

      if (response.statusCode === 200) {
        setOrgRequestMessage({
          type: "success",
          text: "Organization enrollment request sent successfully! You will be notified when approved.",
        })
        setFlashMessage({
          type: "success",
          message: "Organization enrollment request sent successfully!",
        })
        // Refresh dashboard data to get updated organization status
        fetchDashboardData()
        setTimeout(() => {
          setShowOrgEnrollmentModal(false)
          setOrgRequestData({ organisationId: "" })
          setOrgRequestMessage(null)
        }, 2000)
      } else if (response.body?.error?.includes("organisation") || response.body?.message?.includes("organisation")) {
        setOrgRequestMessage({
          type: "error",
          text: "No such organisation exists. Please check the ID and try again.",
          allowRetry: true,
        })
      } else {
        throw new Error(response.body?.error || "Failed to send organization enrollment request")
      }
    } catch (error) {
      console.error("Error sending organization enrollment request:", error)
      setOrgRequestMessage({
        type: "error",
        text: error.message || "Failed to send organization enrollment request. Please try again.",
        allowRetry: true,
      })
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to send organization enrollment request. Please try again.",
      })
    } finally {
      setIsSubmittingOrgRequest(false)
    }
  }

  // Confirm organization request with warning
  const confirmOrgRequest = () => {
    setOrgRequestData({
      ...orgRequestData,
      confirmed: true,
    })
    setOrgRequestMessage(null)
    // Re-submit the form with confirmation
    handleOrgEnrollmentSubmit(new Event("submit"))
  }

  // Replace the getFilteredPendingTasks function and related logic
  const getTasksBreakdown = (tasks) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const currentTasks = tasks.filter((task) => {
      const dueDate = new Date(task.dueDate)
      dueDate.setHours(0, 0, 0, 0)
      return dueDate >= today
    })

    const overdueTasks = tasks.filter((task) => {
      const dueDate = new Date(task.dueDate)
      dueDate.setHours(0, 0, 0, 0)
      return dueDate < today
    })

    return { currentTasks, overdueTasks, totalTasks: tasks.length }
  }

  const quickActions = [
    {
      icon: BookOpen,
      label: "Study Materials",
      color: "from-blue-400 to-cyan-500",
      onClick: () => navigate("/library"),
      disabled: !hasEnrolledClasses,
    },
    {
      icon: ClipboardList,
      label: "View Assignments",
      color: "from-green-400 to-emerald-500",
      onClick: () => navigate("/assignments"),
      disabled: !hasEnrolledClasses,
    },
    {
      icon: Plus,
      label: "Join Class",
      color: "from-orange-400 to-red-500",
      onClick: () => setShowJoinClassModal(true),
      disabled: false, // Always enabled
    },
    {
      icon: Clock,
      label: "Pending Requests",
      color: "from-rose-400 to-pink-500",
      onClick: () => setShowPendingRequestsModal(true),
      disabled: false, // Always enabled
    },
  ]

  const getSubjectColor = (subject) => {
    const colors = {
      Mathematics: "from-purple-500 to-purple-600",
      Science: "from-green-500 to-green-600",
      "Social Science": "from-yellow-500 to-yellow-600",
      English: "from-blue-500 to-blue-600",
      Hindi: "from-orange-500 to-orange-600",
      Physics: "from-violet-500 to-purple-600",
      Chemistry: "from-emerald-500 to-teal-600",
      Biology: "from-emerald-500 to-emerald-600",
      "Computer Science": "from-cyan-500 to-cyan-600",
      maths: "from-purple-500 to-purple-600",
      eng: "from-blue-500 to-blue-600",
      history: "from-yellow-500 to-yellow-600",
      eco: "from-green-500 to-green-600",
      "Artificial Intelligence": "from-indigo-500 to-indigo-600",
      Math: "from-purple-500 to-purple-600",
      History: "from-yellow-500 to-yellow-600",
    }
    return colors[subject] || "from-gray-500 to-gray-600"
  }

  const getSubjectIcon = (subject) => {
    const icons = {
      Mathematics: "📐",
      Science: "🔬",
      "Social Science": "🌍",
      English: "📚",
      Hindi: "🇮🇳",
      Physics: "⚛️",
      Chemistry: "🧪",
      Biology: "🧬",
      "Computer Science": "💻",
      maths: "📐",
      eng: "📚",
      history: "📜",
      eco: "💰",
      "Artificial Intelligence": "🤖",
      Math: "📐",
      History: "📜",
    }
    return icons[subject] || "📖"
  }

  const formatDueDate = (dueDate) => {
    const today = new Date()
    const due = new Date(dueDate)
    const diffTime = due - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return "Today"
    if (diffDays === 1) return "Tomorrow"
    if (diffDays > 1 && diffDays <= 7) return `${diffDays} days`
    return due.toLocaleDateString()
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-50 border-red-200 text-red-800"
      case "medium":
        return "bg-yellow-50 border-yellow-200 text-yellow-800"
      case "low":
        return "bg-green-50 border-green-200 text-green-800"
      default:
        return "bg-gray-50 border-gray-200 text-gray-800"
    }
  }

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-600"
      case "medium":
        return "bg-yellow-100 text-yellow-600"
      case "low":
        return "bg-green-100 text-green-600"
      default:
        return "bg-gray-100 text-gray-600"
    }
  }

  // Class enrollment handlers (separate from organization)
  const handleEnrollmentRequest = async (e) => {
    e.preventDefault()
    setIsEnrolling(true)
    try {
      const response = await sendEnrollmentRequest(newClassCode)
      if (response.statusCode === 201) {
        setFlashMessage({ type: "success", message: response.message || "Enrollment request sent successfully" })
        setNewClassCode("")
        setEnrollmentRequests((prevRequests) => [
          ...prevRequests,
          {
            classCode: newClassCode,
            requestedAt: new Date().toISOString(),
            className: "Pending",
            subject: "Pending",
            teacherName: "Pending",
            enrollmentId: Date.now(),
          },
        ])
        setShowJoinClassModal(false)
        await fetchDashboardData()
      } else {
        throw new Error(response.message || "Failed to send enrollment request")
      }
    } catch (error) {
      console.error("Error sending enrollment request:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to send enrollment request. Please try again.",
      })
    } finally {
      setIsEnrolling(false)
    }
  }

  const handleDeleteRequest = (classCode) => {
    setConfirmDelete(classCode)
  }

  const confirmDeleteRequest = async (enrollmentId) => {
    setIsDeleting(true)
    try {
      const deleteRequest = {
        body: { enrollmentId },
        request: "delete",
      }

      const response = await postAws(`https://api-dev.aurait.ai/enrollment`, "POST", deleteRequest)

      if (response && (response.statusCode === 200 || response.statusCode === 201)) {
        setFlashMessage({ type: "success", message: response.message || "Enrollment request deleted successfully" })
        setEnrollmentRequests((prevRequests) => prevRequests.filter((req) => req.enrollmentId !== enrollmentId))
      } else {
        throw new Error(response?.message || "Failed to delete enrollment request")
      }
    } catch (error) {
      console.error("Error deleting enrollment request:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to delete enrollment request. Please try again.",
      })
    } finally {
      setIsDeleting(false)
      setConfirmDelete(null)
    }
  }

  const cancelDeleteRequest = () => {
    setConfirmDelete(null)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Loader2 size={40} className="text-indigo-600 animate-spin" />
          <p className="text-indigo-800 font-medium text-lg">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center justify-center space-y-4">
          <AlertCircle size={40} className="text-red-600" />
          <p className="text-red-800 font-medium text-lg">Failed to load dashboard data</p>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const { student, stats, classes, pendingTasks, recentGrades, todaysSchedule, analytics } = dashboardData
  const tasksBreakdown = getTasksBreakdown(pendingTasks || [])
  const currentPendingTasks = pendingTasks || []
  const displayedPendingTasks = showAllPendingTasks ? currentPendingTasks : currentPendingTasks.slice(0, 3)

  // Prepare stats for display
  const statsData = [
    {
      title: "Enrolled Classes",
      value: stats.enrolledClasses.toString(),
      subtitle: `${stats.totalAssignments + stats.totalQuizzes} total tasks`,
      icon: BookOpen,
      color: "from-blue-500 to-cyan-500",
      trend: ``,
    },
    {
      title: "Pending Tasks",
      value: stats.pendingTasks.toString(),
      subtitle: `(overdue ${stats.overdueTasks || 0} and currently active ${stats.currentPendingTasks || 0})`,
      icon: CheckSquare,
      color: "from-red-500 to-pink-500",
      trend: `${stats.completedAssignments + stats.completedQuizzes} completed (${stats.completionPercentage}% completed)`,
    },
  ]

  // If no enrolled classes, show the join class modal overlay
  if (!hasEnrolledClasses) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-100 to-blue-200">
        {/* Flash Message */}
        {flashMessage && (
          <div
            className={`fixed top-6 right-6 p-6 rounded-xl shadow-2xl z-[70] flex items-center space-x-3 transform transition-all duration-500 ease-in-out animate-fade-in border-2 ${
              flashMessage.type === "error"
                ? "bg-red-500 border-red-400 text-white"
                : "bg-green-500 border-green-400 text-white"
            } max-w-sm min-w-[300px]`}
            style={{
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)",
            }}
          >
            {flashMessage.type === "error" ? (
              <AlertCircle className="h-6 w-6 flex-shrink-0" />
            ) : (
              <CheckSquare className="h-6 w-6 flex-shrink-0" />
            )}
            <p className="font-medium text-sm leading-relaxed">{flashMessage.message}</p>
            <button
              onClick={() => setFlashMessage(null)}
              className="ml-2 text-white/80 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Animated Background Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-300/30 to-indigo-300/30 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-cyan-300/30 to-blue-300/30 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-indigo-300/20 to-blue-300/20 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "4s" }}
          ></div>
        </div>

        {/* Main Content Overlay */}
        <div className="relative z-10 flex flex-col min-h-screen">
          <main className="flex-1 p-8 overflow-auto">
            <div className="max-w-4xl mx-auto">
              {/* Welcome Section */}
              <div className="bg-white/90 backdrop-blur-sm border border-blue-200 p-6 rounded-3xl shadow-lg mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome, {student.studentName}!</h1>
                    <p className="text-gray-600">Join a class to start your learning journey</p>
                  </div>
                </div>
              </div>

              {/* Join Class Card */}
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden mb-8 border border-blue-200">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 py-8 px-8">
                  <div className="text-center">
                    <BookOpen className="h-16 w-16 text-white mx-auto mb-4" />
                    <h2 className="text-3xl font-bold text-white mb-2">Join a Class to Continue</h2>
                    <p className="text-white/80 text-lg">
                      You need to be enrolled in at least one class to access the dashboard
                    </p>
                  </div>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <button
                      onClick={() => setShowJoinClassModal(true)}
                      className="flex items-center justify-center gap-4 p-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl text-white font-medium shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
                    >
                      <div className="bg-white/20 p-3 rounded-xl">
                        <Plus className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-left">
                        <div className="text-lg font-bold">Join New Class</div>
                        <div className="text-sm text-white/80">Enter class code to join</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setShowPendingRequestsModal(true)}
                      className="flex items-center justify-center gap-4 p-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl text-white font-medium shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
                    >
                      <div className="bg-white/20 p-3 rounded-xl">
                        <Clock className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-left">
                        <div className="text-lg font-bold">View Requests</div>
                        <div className="text-sm text-white/80">Check enrollment status</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Enrollment Status */}
              {enrollmentRequests.length > 0 && (
                <div className="bg-white/90 backdrop-blur-sm border border-blue-200 p-6 rounded-3xl shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-6 w-6 text-orange-500" />
                    <h3 className="text-xl font-bold text-gray-800">Enrollment Requests Status</h3>
                    <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-semibold">
                      {enrollmentRequests.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {enrollmentRequests.slice(0, 3).map((request, index) => (
                      <div
                        key={index}
                        className="bg-orange-50 rounded-xl border border-orange-200 p-4 transition-all hover:shadow-md"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-800">{request.className}</h4>
                            <p className="text-sm text-gray-600">Code: {request.classCode}</p>
                            <p className="text-xs text-gray-500">
                              Requested: {new Date(request.requestedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-3 py-1 rounded-full bg-orange-100 text-orange-600 font-medium">
                              Pending
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {enrollmentRequests.length > 3 && (
                      <button
                        onClick={() => setShowPendingRequestsModal(true)}
                        className="w-full p-3 text-center text-orange-600 hover:text-orange-800 font-medium text-sm border border-orange-200 rounded-xl hover:bg-orange-50 transition-colors"
                      >
                        View All Requests ({enrollmentRequests.length})
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        {/* Join Class Modal */}
        {showJoinClassModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-emerald-500 to-green-500 py-6 px-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Plus className="h-6 w-6 text-white" />
                    <div>
                      <h2 className="text-2xl font-bold text-white">Join New Class</h2>
                      <p className="text-white/80 text-sm mt-1">Enter class code to send enrollment request</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowJoinClassModal(false)
                      setNewClassCode("")
                    }}
                    className="text-white hover:text-white/80 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="p-8">
                <form onSubmit={handleEnrollmentRequest} className="space-y-6">
                  <div>
                    <label htmlFor="classCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Class Code
                    </label>
                    <input
                      type="text"
                      id="classCode"
                      value={newClassCode}
                      onChange={(e) => setNewClassCode(e.target.value)}
                      placeholder="Enter class code provided by teacher"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                      required
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowJoinClassModal(false)
                        setNewClassCode("")
                      }}
                      className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isEnrolling}
                      className="flex-1 px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-300 flex items-center justify-center font-medium"
                    >
                      {isEnrolling ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={18} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Plus className="mr-2" size={18} />
                          Send Request
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Pending Class Requests Modal */}
        {showPendingRequestsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-6 px-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Clock className="h-6 w-6 text-white" />
                    <div>
                      <h2 className="text-2xl font-bold text-white">Pending Class Enrollment Requests</h2>
                      <p className="text-white/80 text-sm mt-1">{enrollmentRequests.length} pending requests</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPendingRequestsModal(false)}
                    className="text-white hover:text-white/80 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8">
                <div className="space-y-4">
                  {enrollmentRequests.length > 0 ? (
                    enrollmentRequests.map((request, index) => (
                      <div
                        key={index}
                        className="bg-amber-50 rounded-xl border border-amber-200 p-6 transition-all hover:shadow-md"
                      >
                        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">{request.className}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-amber-600" />
                                <span className="text-gray-700">Code: {request.classCode}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <BookOpen className="h-4 w-4 text-amber-600" />
                                <span className="text-gray-700">Subject: {request.subject}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-amber-600" />
                                <span className="text-gray-700">Teacher: {request.teacherName}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-amber-600" />
                                <span className="text-gray-700">
                                  Requested: {new Date(request.requestedAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          {confirmDelete === request.classCode ? (
                            <div className="flex flex-col gap-3 bg-white p-4 rounded-lg shadow-md">
                              <p className="text-red-600 text-sm font-medium">
                                Are you sure you want to delete this request?
                              </p>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => confirmDeleteRequest(request.enrollmentId)}
                                  className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 transition duration-300 flex items-center justify-center text-sm"
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <>
                                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                      Deleting...
                                    </>
                                  ) : (
                                    <>
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={cancelDeleteRequest}
                                  className="bg-gray-200 text-gray-800 px-3 py-2 rounded-md hover:bg-gray-300 transition duration-300 text-sm"
                                  disabled={isDeleting}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleDeleteRequest(request.classCode)}
                              className="bg-white text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50 transition duration-300 flex items-center text-sm"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Request
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-600 mb-2">No Pending Requests</h3>
                      <p className="text-gray-500">You don't have any pending class enrollment requests.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6 border-t flex justify-end">
                <button
                  onClick={() => setShowPendingRequestsModal(false)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Original dashboard content when user has enrolled classes
  return (
    <div className="min-h-screen bg-blue-100">
      {/* Flash Message */}
      {flashMessage && (
        <div
          className={`fixed top-6 right-6 p-6 rounded-xl shadow-2xl z-[60] flex items-center space-x-3 transform transition-all duration-500 ease-in-out animate-fade-in border-2 ${
            flashMessage.type === "error"
              ? "bg-red-500 border-red-400 text-white"
              : "bg-green-500 border-green-400 text-white"
          } max-w-sm min-w-[300px]`}
          style={{
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)",
          }}
        >
          {flashMessage.type === "error" ? (
            <AlertCircle className="h-6 w-6 flex-shrink-0" />
          ) : (
            <CheckSquare className="h-6 w-6 flex-shrink-0" />
          )}
          <p className="font-medium text-sm leading-relaxed">{flashMessage.message}</p>
          <button
            onClick={() => setFlashMessage(null)}
            className="ml-2 text-white/80 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-pink-300/20 to-rose-300/20 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-sky-300/20 to-cyan-300/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-purple-300/10 to-pink-300/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        ></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <main className="flex-1 p-8 overflow-auto">
          <div className="space-y-8">
            {/* Welcome Section */}
            <div className="bg-white border border-gray-200 p-6 rounded-3xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome back, {student.studentName}!</h1>
                  <p className="text-gray-600">Ready to continue your learning journey?</p>
                </div>
                <div className="text-right"></div>
              </div>
            </div>

            {/* Student Profile Card with Organization Status */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 transition-all duration-300 hover:shadow-2xl">
              <div className="md:flex">
                {/* Profile Image Section */}
                <div className="md:w-1/3 lg:w-1/4 relative">
                  <div className="h-56 md:h-64 w-full relative overflow-hidden">
                    <div className="h-full w-full bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center">
                      <User size={80} className="text-blue-400" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h1 className="text-white text-xl md:text-2xl font-bold tracking-tight drop-shadow-md">
                        {student?.studentName || "Student Name"}
                      </h1>
                    </div>
                  </div>
                </div>

                {/* Profile Info Section */}
                <div className="p-4 md:p-6 flex-grow">
                  <div className="flex flex-col h-full">
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-4">
                        <div className="uppercase tracking-wide text-sm text-blue-600 font-bold bg-blue-50 px-3 py-1 rounded-full inline-block">
                          Student Dashboard
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Email and Phone row */}
                        <div className="flex flex-col md:flex-row gap-3">
                          <div className="flex items-center text-gray-700 bg-gray-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-gray-100 flex-1">
                            <User className="w-4 h-4 mr-2 text-blue-500" />
                            <div>
                              <p className="text-xs text-gray-500 font-medium">Email Address</p>
                              <p className="font-medium text-sm">{student?.studentEmail || "Email not provided"}</p>
                            </div>
                          </div>
                          <div className="flex items-center text-gray-700 bg-gray-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-gray-100 flex-1">
                            <User className="w-4 h-4 mr-2 text-blue-500" />
                            <div>
                              <p className="text-xs text-gray-500 font-medium">Phone Number</p>
                              <p className="font-medium text-sm">
                                {student?.phoneNumber || "Phone number not provided"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Organization section - completely separate from class enrollment */}
                        {studentOrgStatus === "approved" ? (
                          <div className="flex items-center text-gray-700 bg-green-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-green-100">
                            <Building2 className="w-4 h-4 mr-2 text-green-500" />
                            <div className="flex-grow">
                              <p className="text-xs text-gray-500 font-medium">Organisation</p>
                              <div className="flex items-center">
                                <p className="font-medium text-sm">{studentOrgName || "Not Available"}</p>
                                <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full flex items-center gap-1">
                                  <Check className="h-3 w-3" />
                                  Joined
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => setShowOrgStatusModal(true)}
                              className="ml-2 text-xs text-green-600 hover:text-green-700 underline"
                            >
                              View Details
                            </button>
                          </div>
                        ) : studentOrgStatus === "sent" ? (
                          <div className="flex items-center text-gray-700 bg-blue-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-blue-100">
                            <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                            <div className="flex-grow">
                              <p className="text-xs text-gray-500 font-medium">Organisation</p>
                              <div className="flex items-center">
                                <p className="font-medium text-sm">{studentOrgName || "Request Sent"}</p>
                                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Pending
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => setShowOrgStatusModal(true)}
                              className="ml-2 text-xs text-blue-600 hover:text-blue-700 underline"
                            >
                              View Status
                            </button>
                          </div>
                        ) : studentOrgStatus === "rejected" ? (
                          <div className="flex items-center text-gray-700 bg-red-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-red-100">
                            <Building2 className="w-4 h-4 mr-2 text-red-500" />
                            <div className="flex-grow">
                              <p className="text-xs text-gray-500 font-medium">Organisation</p>
                              <div className="flex items-center">
                                <p className="font-medium text-sm">{studentOrgName || "Request Rejected"}</p>
                                <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full flex items-center gap-1">
                                  <X className="h-3 w-3" />
                                  Rejected
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => setShowOrgEnrollmentModal(true)}
                              className="ml-2 text-xs text-red-600 hover:text-red-700 underline"
                            >
                              Try Again
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowOrgEnrollmentModal(true)}
                            className="flex items-center justify-center text-blue-600 bg-blue-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-blue-100 w-full"
                          >
                            <Building2 className="w-4 h-4 mr-2" />
                            <span className="font-medium text-sm">Join Organisation</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {statsData.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 p-8 rounded-3xl hover:bg-gray-50 transition-all group hover:scale-105 shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`p-4 bg-gradient-to-r ${stat.color} rounded-2xl shadow-md group-hover:scale-110 transition-transform`}
                    >
                      <stat.icon className="h-7 w-7 text-white" />
                    </div>
                  </div>
                  <h3 className="text-gray-500 text-sm font-medium mb-2">{stat.title}</h3>
                  <p className="text-4xl font-bold text-gray-800 mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-sm">{stat.subtitle}</p>
                  <p className="text-indigo-600 text-xs mt-1 font-medium">{stat.trend}</p>
                </div>
              ))}
            </div>

            {/* Dashboard Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Quick Actions */}
              <div className="bg-white border border-gray-200 p-6 rounded-3xl shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Zap className="h-6 w-6 text-yellow-500" />
                    <h3 className="text-xl font-bold text-gray-800">Quick Actions</h3>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.disabled ? undefined : action.onClick}
                      disabled={action.disabled}
                      className={`flex items-center gap-4 p-4 bg-gradient-to-r ${action.color} rounded-2xl text-white font-medium shadow-md transition-all duration-200 ${
                        action.disabled ? "opacity-50 cursor-not-allowed" : "hover:shadow-xl hover:scale-[1.02]"
                      }`}
                    >
                      <div className="bg-white/20 p-2 rounded-xl">
                        <action.icon className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-sm">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Pending Tasks */}
              <div className="bg-white border border-gray-200 p-6 rounded-3xl shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-6 w-6 text-red-500" />
                    <h3 className="text-xl font-bold text-gray-800">Pending Tasks</h3>
                  </div>
                  <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                    {stats.currentPendingTasks || 0}
                  </span>
                </div>
                <div className="space-y-3">
                  {currentPendingTasks.length > 0 ? (
                    <>
                      {displayedPendingTasks.map((task) => {
                        const classInfo = classes.find((c) => c.classRoomId === task.classRoomId)
                        return (
                          <div
                            key={task.id}
                            className={`p-4 rounded-xl border transition-all hover:shadow-md cursor-pointer ${getPriorityColor(task.priority)}`}
                            onClick={() => {
                              // Navigate based on task status
                              if (task.type === "assignment") {
                                if (task.hasStarted || task.isSaved || task.status === "saved") {
                                  navigate(`/submitted-assignment/${task.id}`)
                                } else {
                                  navigate(`/assignment/details/${task.id}`)
                                }
                              } else if (task.type === "quiz") {
                                if (task.hasStarted || task.isSaved || task.status === "saved") {
                                  navigate(`/quiz/submitted/${task.id}`)
                                } else {
                                  navigate(`/quiz/take/${task.id}`)
                                }
                              }
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-800 text-sm">{task.title}</h4>
                              <span className="text-xs text-gray-500 capitalize">{task.type}</span>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">
                              {classInfo
                                ? `${classInfo.subject} - ${classInfo.classValue}${classInfo.section}`
                                : "Unknown Class"}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Due: {formatDueDate(task.dueDate)}</span>
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${getPriorityBadgeColor(task.priority)}`}
                              >
                                {task.priority}
                              </span>
                              {(task.hasStarted || task.isSaved || task.status === "saved") && (
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-600 ml-2">
                                  Started
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                      {currentPendingTasks.length > 3 && (
                        <button
                          onClick={() => setShowAllPendingTasksModal(true)}
                          className="w-full p-3 text-center text-indigo-600 hover:text-indigo-800 font-medium text-sm border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
                        >
                          See More ({currentPendingTasks.length - 3} more) <ChevronDown className="h-4 w-4" />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <AlertCircle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                      <p className="text-gray-800 font-semibold">{stats.pendingTasks} Total Pending</p>
                      <p className="text-orange-600 text-sm">{stats.overdueTasks || 0} overdue tasks</p>
                      <p className="text-gray-500 text-xs mt-1">No current tasks available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Enrolled Classes */}
            <div className="bg-white border border-gray-200 p-8 rounded-3xl shadow-lg">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl">
                    <GraduationCap className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">My Classes</h3>
                </div>
                <div className="text-gray-600 text-sm">
                  {classes.length} {classes.length === 1 ? "class" : "classes"} enrolled
                </div>
              </div>
              {classes && classes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {classes.map((classItem) => (
                    <div
                      key={classItem.classRoomId}
                      className="relative bg-gray-50 p-6 rounded-3xl border border-gray-200 hover:bg-white hover:border-gray-300 transition-all group hover:scale-105 shadow-md cursor-pointer"
                      onClick={() => {
                        setSelectedClass(classItem)
                        setShowClassDetails(true)
                      }}
                    >
                      {/* Class Code - Top Right */}
                      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                        <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-full border border-gray-300 shadow-sm">
                          {classItem.classCode}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigator.clipboard.writeText(classItem.classCode)
                            setFlashMessage({
                              type: "success",
                              message: "Class code copied!",
                            })
                          }}
                          title="Copy class code"
                          className="text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          <Copy size={14} />
                        </button>
                      </div>

                      <div className="pr-20 mb-6">
                        <div className="flex items-start gap-4">
                          <div className="text-3xl">{getSubjectIcon(classItem.subject)}</div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-gray-800 text-lg">
                              {classItem.subject.length > 4
                                ? `${classItem.subject.substring(0, 4)}...`
                                : classItem.subject}{" "}
                              - {classItem.classValue}
                              {classItem.section}
                            </h4>
                            {studentOrgStatus === "approved" && studentOrgName && (
                              <p className="text-gray-500 text-sm mt-1">
                                <span className="font-medium text-blue-600">{studentOrgName}</span>
                              </p>
                            )}
                            <p className="text-gray-500 text-sm">
                              {classItem.classDescription
                                ? classItem.classDescription.length > 20
                                  ? `${classItem.classDescription.substring(0, 20)}...`
                                  : classItem.classDescription
                                : classItem.subject.length > 20
                                  ? `${classItem.subject.substring(0, 20)}...`
                                  : classItem.subject}
                            </p>
                            <p className="text-gray-400 text-xs mt-1">
                              {classItem.enrolledStudentCount} students enrolled
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 block">Assignments</span>
                          <p className="text-gray-800 font-semibold">
                            {classItem.totalAssignments === 0
                              ? "0/0"
                              : `${classItem.completedAssignments}/${classItem.totalAssignments}`}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 block">Quizzes</span>
                          <p className="text-gray-800 font-semibold">
                            {classItem.totalQuizzes === 0
                              ? "0/0"
                              : `${classItem.completedQuizzes}/${classItem.totalQuizzes}`}
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar - only show if there are tasks */}
                      {classItem.totalAssignments > 0 || classItem.totalQuizzes > 0 ? (
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Progress</span>
                            <span>
                              {Math.round(
                                ((classItem.completedAssignments + classItem.completedQuizzes) /
                                  (classItem.totalAssignments + classItem.totalQuizzes)) *
                                  100,
                              )}
                              %
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full bg-gradient-to-r ${getSubjectColor(classItem.subject)}`}
                              style={{
                                width: `${
                                  ((classItem.completedAssignments + classItem.completedQuizzes) /
                                    (classItem.totalAssignments + classItem.totalQuizzes)) *
                                  100
                                }%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 text-center py-2">
                          <p className="text-gray-500 text-xs">No assignment/quiz assigned to track</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Classes Enrolled</h3>
                  <p className="text-gray-500">Join a class to start your learning journey!</p>
                </div>
              )}
            </div>

            {/* Analytics Button */}
            <div className="bg-white border border-gray-200 p-6 rounded-3xl shadow-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-2xl">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Performance Analytics</h3>
                    <p className="text-gray-600 text-sm">View detailed insights about your progress</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/student/details")}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center gap-2 font-medium"
                >
                  <BarChart3 className="h-5 w-5" />
                  View Analytics
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Organization Enrollment Modal - Completely separate from class enrollment */}
      {showOrgEnrollmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 py-6 px-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Building2 className="h-6 w-6 text-white" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Join Organisation</h2>
                    <p className="text-white/80 text-sm mt-1">Enter organisation ID to send enrollment request</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowOrgEnrollmentModal(false)
                    setOrgRequestData({ organisationId: "" })
                    setOrgRequestMessage(null)
                  }}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-8">
              {orgRequestMessage ? (
                <div
                  className={`p-4 rounded-md mb-4 ${
                    orgRequestMessage.type === "success"
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : orgRequestMessage.type === "warning"
                        ? "bg-yellow-50 text-yellow-800 border border-yellow-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  <p className="text-sm font-medium">{orgRequestMessage.text}</p>
                  {orgRequestMessage.needsConfirmation && (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={() => setOrgRequestMessage(null)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmOrgRequest}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                      >
                        Yes, Continue Anyway
                      </button>
                    </div>
                  )}
                  {orgRequestMessage.allowRetry && (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={() => setOrgRequestMessage(null)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                      >
                        Try Again
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleOrgEnrollmentSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="organisationId" className="block text-sm font-medium text-gray-700 mb-2">
                      Organisation ID
                    </label>
                    <input
                      type="text"
                      id="organisationId"
                      value={orgRequestData.organisationId}
                      onChange={(e) => setOrgRequestData({ ...orgRequestData, organisationId: e.target.value })}
                      placeholder="Enter organisation ID"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOrgEnrollmentModal(false)
                        setOrgRequestData({ organisationId: "" })
                        setOrgRequestMessage(null)
                      }}
                      className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingOrgRequest}
                      className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-300 flex items-center justify-center font-medium"
                    >
                      {isSubmittingOrgRequest ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={18} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Building2 className="mr-2" size={18} />
                          Send Request
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Organization Status Modal */}
      {showOrgStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 py-6 px-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Building2 className="h-6 w-6 text-white" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Organisation Details</h2>
                    <p className="text-white/80 text-sm mt-1">Your organisation enrollment information</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowOrgStatusModal(false)}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-8">
              {studentOrgStatus === "approved" ? (
                <div className="p-4 bg-green-50 text-green-800 rounded-lg border border-green-200 text-center">
                  <div className="flex items-center justify-center mb-3">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <p className="font-medium text-lg mb-2">Successfully Joined!</p>
                  <p className="text-sm">
                    You are now a member of <span className="font-semibold">{studentOrgName}</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-2">Organisation ID: {studentOrgId}</p>
                </div>
              ) : studentOrgStatus === "sent" ? (
                <div className="p-4 bg-blue-50 text-blue-800 rounded-lg border border-blue-200 text-center">
                  <div className="flex items-center justify-center mb-3">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                  </div>
                  <p className="font-medium text-lg mb-2">Request Pending</p>
                  <p className="text-sm">
                    Your request to join <span className="font-semibold">{studentOrgName}</span> is awaiting approval.
                  </p>
                  <p className="text-xs text-gray-600 mt-2">Organisation ID: {studentOrgId}</p>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 text-gray-800 rounded-lg border border-gray-200 text-center">
                  <p className="font-medium">No organisation information available.</p>
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowOrgStatusModal(false)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Join Class Modal - Only for class enrollment */}
      {showJoinClassModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-500 to-green-500 py-6 px-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Plus className="h-6 w-6 text-white" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Join New Class</h2>
                    <p className="text-white/80 text-sm mt-1">Enter class code to send enrollment request</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowJoinClassModal(false)
                    setNewClassCode("")
                  }}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-8">
              <form onSubmit={handleEnrollmentRequest} className="space-y-6">
                <div>
                  <label htmlFor="classCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Class Code
                  </label>
                  <input
                    type="text"
                    id="classCode"
                    value={newClassCode}
                    onChange={(e) => setNewClassCode(e.target.value)}
                    placeholder="Enter class code provided by teacher"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowJoinClassModal(false)
                      setNewClassCode("")
                    }}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEnrolling}
                    className="flex-1 px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-300 flex items-center justify-center font-medium"
                  >
                    {isEnrolling ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2" size={18} />
                        Send Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Pending Class Requests Modal */}
      {showPendingRequestsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-6 px-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Clock className="h-6 w-6 text-white" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Pending Class Enrollment Requests</h2>
                    <p className="text-white/80 text-sm mt-1">{enrollmentRequests.length} pending requests</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPendingRequestsModal(false)}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8">
              <div className="space-y-4">
                {enrollmentRequests.length > 0 ? (
                  enrollmentRequests.map((request, index) => (
                    <div
                      key={index}
                      className="bg-amber-50 rounded-xl border border-amber-200 p-6 transition-all hover:shadow-md"
                    >
                      <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800 mb-3">{request.className}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-amber-600" />
                              <span className="text-gray-700">Code: {request.classCode}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <BookOpen className="h-4 w-4 text-amber-600" />
                              <span className="text-gray-700">Subject: {request.subject}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-amber-600" />
                              <span className="text-gray-700">Teacher: {request.teacherName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-amber-600" />
                              <span className="text-gray-700">
                                Requested: {new Date(request.requestedAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        {confirmDelete === request.classCode ? (
                          <div className="flex flex-col gap-3 bg-white p-4 rounded-lg shadow-md">
                            <p className="text-red-600 text-sm font-medium">
                              Are you sure you want to delete this request?
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => confirmDeleteRequest(request.enrollmentId)}
                                className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 transition duration-300 flex items-center justify-center text-sm"
                                disabled={isDeleting}
                              >
                                {isDeleting ? (
                                  <>
                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </>
                                )}
                              </button>
                              <button
                                onClick={cancelDeleteRequest}
                                className="bg-gray-200 text-gray-800 px-3 py-2 rounded-md hover:bg-gray-300 transition duration-300 text-sm"
                                disabled={isDeleting}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleDeleteRequest(request.classCode)}
                            className="bg-white text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50 transition duration-300 flex items-center text-sm"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Request
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Pending Requests</h3>
                    <p className="text-gray-500">You don't have any pending class enrollment requests.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setShowPendingRequestsModal(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Pending Tasks Modal */}
      {showAllPendingTasksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-500 to-pink-500 py-6 px-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <AlertCircle className="h-6 w-6 text-white" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">All Pending Tasks</h2>
                    <p className="text-white/80 text-sm mt-1">{currentPendingTasks.length} tasks requiring attention</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAllPendingTasksModal(false)}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8">
              <div className="space-y-4">
                {currentPendingTasks.length > 0 ? (
                  currentPendingTasks.map((task) => {
                    const classInfo = classes.find((c) => c.classRoomId === task.classRoomId)
                    return (
                      <div
                        key={task.id}
                        className={`p-6 rounded-xl border transition-all hover:shadow-lg ${getPriorityColor(task.priority)}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-800 text-lg mb-2">{task.title}</h4>
                            <p className="text-gray-600 mb-3">
                              {classInfo
                                ? `${classInfo.subject} - ${classInfo.classValue}${classInfo.section}`
                                : "Unknown Class"}
                            </p>
                            {task.description && <p className="text-gray-600 text-sm mb-3">{task.description}</p>}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-1 rounded-full">
                              {task.type}
                            </span>
                            <span
                              className={`text-xs px-3 py-1 rounded-full font-medium ${getPriorityBadgeColor(task.priority)}`}
                            >
                              {task.priority} priority
                            </span>
                            {(task.hasStarted || task.isSaved || task.status === "saved") && (
                              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-600 ml-2">
                                Started
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-600">Due: {formatDueDate(task.dueDate)}</span>
                            </div>
                            {task.maxMarks && (
                              <div className="flex items-center gap-1">
                                <BarChart3 className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-600">Max: {task.maxMarks} marks</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              // Navigate based on task status
                              if (task.type === "assignment") {
                                if (task.hasStarted || task.isSaved || task.status === "saved") {
                                  navigate(`/submitted-assignment/${task.id}`)
                                } else {
                                  navigate(`/assignment/details/${task.id}`)
                                }
                              } else if (task.type === "quiz") {
                                if (task.hasStarted || task.isSaved || task.status === "saved") {
                                  navigate(`/quiz/submitted/${task.id}`)
                                } else {
                                  navigate(`/quiz/take/${task.id}`)
                                }
                              }
                              setShowAllPendingTasksModal(false)
                            }}
                            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="text-center py-12">
                    <CheckSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Pending Tasks</h3>
                    <p className="text-gray-500">Great job! You're all caught up with your assignments.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setShowAllPendingTasksModal(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentDashboard
